/* eslint-disable no-param-reassign */
import { type Ref, type ShallowReactive, computed } from 'vue'

import type {
  FinderBudgetCriterion,
  FinderConfig,
  FinderCriterion,
  FinderOption,
  FinderRequirementsCriterion,
} from './useFinder.types'

type FacetCriterion = Exclude<
  FinderCriterion,
  FinderBudgetCriterion | FinderRequirementsCriterion
>

/**
 * Returns the first criterion of the given `kind` from a Finder config, narrowed
 * to its specific criterion type. Keeps the lookup (and its type guard) out of
 * the consuming components.
 */
export function findCriterion<T extends FinderCriterion['kind']>(
  config: FinderConfig,
  kind: T,
): Extract<FinderCriterion, { kind: T }> | undefined {
  return config.criteria.find(
    (criterion): criterion is Extract<FinderCriterion, { kind: T }> =>
      criterion.kind === kind,
  )
}

/**
 * Maps Finder UI interactions to the shared `filtersInput`/`priceInput` consumed
 * by `useSearchInputs` and `useProductsSearch`. Writes by reassignment so the
 * existing pipeline picks up Finder selections unmodified.
 */
export function useFinder(
  config: FinderConfig,
  filtersInput: ShallowReactive<Record<string, string[]>>,
  priceInput: Ref<[number, number] | undefined>,
  inventoryFloor = 0,
) {
  const facetCriteria = computed(() =>
    config.criteria.filter(
      (criterion): criterion is FacetCriterion =>
        criterion.kind !== 'budget' && criterion.kind !== 'requirements',
    ),
  )
  const budgetCriterion = computed(() =>
    config.criteria.find(
      (criterion): criterion is FinderBudgetCriterion =>
        criterion.kind === 'budget',
    ),
  )

  function isOptionActive(
    criterion: FacetCriterion,
    option: FinderOption,
  ): boolean {
    if (option.values.length === 0) return false

    const currentValues = filtersInput[criterion.facetName] ?? []

    return option.values.every((v) => currentValues.includes(v))
  }

  function isOptionSelected(
    criterion: FacetCriterion,
    optionId: string,
  ): boolean {
    const option = criterion.options.find((o) => o.id === optionId)

    return option ? isOptionActive(criterion, option) : false
  }

  function toggleOption(criterion: FacetCriterion, optionId: string): void {
    const option = criterion.options.find((o) => o.id === optionId)
    if (!option) return

    const currentValues = filtersInput[criterion.facetName] ?? []

    if (isOptionActive(criterion, option)) {
      filtersInput[criterion.facetName] = currentValues.filter(
        (v) => !option.values.includes(v),
      )
    } else {
      const merged = [...currentValues, ...option.values]
      filtersInput[criterion.facetName] = [...new Set(merged)]
    }
  }

  /**
   * Replaces the full set of facet values for a criterion in one shot (used by
   * the size range, which selects every option whose numeric id falls within a
   * [`rangeMin`, `rangeMax`] window). Assigns a fresh array so the
   * `shallowReactive` `filtersInput` reactivity fires on every call, including
   * when `values` is empty (which clears the facet).
   */
  function setCriterionValues(
    criterion: FacetCriterion,
    values: string[],
  ): void {
    filtersInput[criterion.facetName] = [...values]
  }

  function isBudgetPresetSelected(presetId: string): boolean {
    if (!priceInput.value) return false

    const preset = budgetCriterion.value?.presets.find((p) => p.id === presetId)
    if (!preset) return false

    return (
      priceInput.value[0] === inventoryFloor &&
      priceInput.value[1] === preset.maxValue
    )
  }

  function selectBudgetPreset(presetId: string): void {
    const preset = budgetCriterion.value?.presets.find((p) => p.id === presetId)
    if (!preset) return

    if (isBudgetPresetSelected(presetId)) {
      priceInput.value = undefined
    } else {
      priceInput.value = [inventoryFloor, preset.maxValue]
    }
  }

  function resetCriterion(criterion: FinderCriterion): void {
    if (criterion.kind === 'budget') {
      priceInput.value = undefined
    } else if (criterion.kind !== 'requirements') {
      filtersInput[criterion.facetName] = []
    }
  }

  function reset(): void {
    for (const criterion of config.criteria) {
      resetCriterion(criterion)
    }
  }

  const isFinderActive = computed(() =>
    config.criteria.some((criterion) => {
      if (criterion.kind === 'budget') {
        return priceInput.value !== undefined
      }

      if (criterion.kind === 'requirements') {
        return false
      }

      return (filtersInput[criterion.facetName]?.length ?? 0) > 0
    }),
  )

  const selectedOptionsCount = computed(() =>
    facetCriteria.value.reduce((count, criterion) => {
      return count + (filtersInput[criterion.facetName]?.length ?? 0)
    }, 0),
  )

  return {
    isFinderActive,
    selectedOptionsCount,
    isOptionSelected,
    toggleOption,
    setCriterionValues,
    isBudgetPresetSelected,
    selectBudgetPreset,
    resetCriterion,
    reset,
  }
}
